<script setup>
import { onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { getEssay, patchEssayLike } from '@/api/EowayApi';
import { BoardTitleBox, Pagination } from '@/components/board';
import { IconLike } from '@/components/svg';
import { useStore } from 'vuex';
import { addContentLogAPI } from '@/api/logServiceApi';
import { useDeviceDetect } from '@/composables/useDeviceDetect';
import SearchBox from './SearchBox.vue';
import SearchBoxWithList from './SearchBoxWithList.vue';

const { isMobile } = useDeviceDetect();
const props = defineProps({
  board: {
    type: String,
    default: '',
  },
  boardId: {
    type: Number,
    default: 0,
  },
});

const router = useRouter();
const store = useStore();
const isPopSearchNoneBox = ref(false);
/** 게시물 목록 */
const listData = ref([]);
/** 에세이 정렬 타입 */
const essaySortOptions = [
  { key: 'new', value: 'displayDate', text: '최신순' },
  { key: 'hit', value: 'views', text: '조회순' },
  { key: 'fav', value: 'likes', text: '좋아요순' },
];
/** 선택된 에세이 정렬 */
const selectedSort = ref(essaySortOptions[0].key);
/** 에세이 정렬 방향 상태 */
const sortDirections = ref({
  new: 'desc',
  hit: 'desc',
  fav: 'desc',
});
const totalPages = ref(0);
const totalElements = ref(0);
/** 최근 검색어 */
// 최근검색어 key
const recentKey = computed(() => `board-${props.board ?? 'default'}`);
const recent = useRecentSearches(recentKey, { max: 10, ttlHours: 480, storage: 'local' });

/** 인디케이터 동작 여부 */
const isLoading = ref(false);
const searchKeywords = ref('');
/** 에세이 요청 파라미터 */
const paramData = ref({
  isOpenAppBoard: true,
  search: '',
  page: 1,
  size: 9,
  sort: 'displayDate.desc',
  systemId: import.meta.env.VITE_APP_SYSTEMID,
});

onMounted(async () => {
  document.addEventListener('pointerdown', onDocPointer);
  document.addEventListener('keydown', onDocKey);
  store.dispatch('saveCurrentBoard', props.board);
  store.dispatch('saveEssayFocus', { key: 'list', value: true });
  // const currentEssayPage = store.state.boards.currentEssayPage
  // const currentEssayKeywords = store.state.boards.currentEssayKeywords
  const { currentEssayPage, currentEssayKeywords, currentEssaySort } = store.state.boards;

  if (currentEssayPage) {
    paramData.value.page = currentEssayPage;
  }
  if (currentEssayKeywords) {
    // paramData.value.search = currentEssayKeywords
    searchKeywords.value = currentEssayKeywords;
  }
  restoreSortFromStore(currentEssaySort);
  await getList();
});

onUnmounted(async () => {
  await store.dispatch('saveEssayFocus', { key: 'list', value: false });
  document.removeEventListener('pointerdown', onDocPointer);
  document.removeEventListener('keydown', onDocKey);
  // store.dispatch('saveCurrentEssayKeywords', '')
});

/** 에세이 목록 요청 */
const getList = async () => {
  isLoading.value = true;
  await nextTick();
  // const query =
  //   typeof q === 'string' ? q : (searchKeywords.value ?? '')
  // if (typeof q === 'string') {
  //   searchKeywords.value = q
  // }
  paramData.value.search = searchKeywords.value;
  try {
    const result = await getEssay({ body: paramData.value });
    if (searchKeywords.value) {
      recent.add(searchKeywords.value);
      store.dispatch('saveCurrentEssayKeywords', searchKeywords.value);
    }
    if (result) {
      // 검색 결과가 없을 경우
      if (searchKeywords.value && result.content.length === 0) {
        totalElements.value = result.totalElements;
        setPopSearchNoneBox(true);
        return;
      }

      listData.value = result.content;
      totalPages.value = result.totalPages;
      totalElements.value = result.totalElements;
      isPopSearchNoneBox.value = false;
    }
  } catch (error) {
    console.error('Failed to fetch list:', error);
    alert('An error occurred while fetching data.');
  } finally {
    isLoading.value = false;
  }
};
/** 검색어 업데이트 */
// const updateSearchKeywords = (newValue) => (searchKeywords.value = newValue)
const updateSearchKeywords = (newValue) => {
  const next = (newValue ?? '').trim();
  const curr = (searchKeywords.value ?? '').trim();
  if (next === curr) return;
  searchKeywords.value = newValue;
  paramData.value.page = 1;
  store.dispatch('saveCurrentEssayPage', paramData.value.page);
};

/** 에세이 정렬 변경 */
const sort = (sortOption) => {
  if (selectedSort.value === sortOption.key)
    sortDirections.value[sortOption.key] =
      sortDirections.value[sortOption.key] === 'desc' ? 'asc' : 'desc';
  else sortDirections.value[sortOption.key] = 'desc';

  paramData.value.sort = `${sortOption.value}.${sortDirections.value[sortOption.key]}`;
  selectedSort.value = sortOption.key;

  store.dispatch('saveCurrentEssaySort', {
    key: selectedSort.value,
    dir: sortDirections.value[selectedSort.value],
  });

  getList();
};

/** 게시물로 이동  */
const goView = (essayId) => {
  addContentLogAPI({
    contentType: 'N_essay_article',
    servicePk: essayId,
  });
  router.push(`/nangman-center/essay/${essayId}`);
};

/** 좋아요 버튼 클릭 - 이벤트 버블링 중단 */
const handleBubbling = (event, essayId) => {
  event.stopPropagation();
  patchLike(essayId);
};

/** 좋아요 업데이트 */
const patchLike = async (essayId) => {
  const result = await patchEssayLike(essayId);
  const clickedEssay = listData.value.find((item) => item.essayId === essayId);
  clickedEssay.likesChecked = result.likeClicked ? 1 : 0;
};

/** 페이지 변경 */
const changePage = (page) => {
  paramData.value.page = page;
  getList();
  store.dispatch('saveCurrentEssayPage', page);
};

const divStyle = (item) => {
  if (item.attachFiles && item.attachFiles.length) return null;
  return {
    width: '100%',
    height: '100%',
    borderRadius: '8px',
    background: `linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.40) 100%), ${item.backgroundColor}`,
  };
};

/** 드롭다운 박스 관련 로직 */
const open = ref(false);
const ddRef = ref(null);

const currentOption = computed(() => {
  return essaySortOptions.find((o) => o.key === selectedSort.value) || essaySortOptions[0];
});
const toggle = () => {
  open.value = !open.value;
};
const close = () => {
  open.value = false;
};
const choose = (opt) => {
  if (selectedSort.value !== opt.key) {
    sort(opt);
  }
  close();
};
// 바깥 클릭/포인터 시 닫기
const onDocPointer = (e) => {
  if (!ddRef.value) return;
  if (!ddRef.value.contains(e.target)) close();
};
const onDocKey = (e) => {
  if (e.key === 'Escape') close();
};

/** 검색창 X 클릭시 초기화 */
// 이미 초기 상태일 경우 다시 요청하지 않도록 조건 검사
function onSearchClear() {
  const p = paramData.value;
  const defKeyword = '';
  const isInitial = (p.search ?? '') === defKeyword;

  if (isInitial) return;
  Object.assign(p, { search: searchKeywords.value, page: 1 });
  store.dispatch('saveCurrentEssayKeywords', '');
  getList();
}

/** 검색 결과 없음 */
const setPopSearchNoneBox = (state) => (isPopSearchNoneBox.value = state);

/** 졍렬 스토어 관련 헬퍼함수 */
function restoreSortFromStore(saved) {
  const defaultKey = essaySortOptions[0].key;
  const defaultDir = 'desc';

  const savedKey = saved?.key;
  const savedDir = saved?.dir;

  const validKey = essaySortOptions.some((o) => o.key === savedKey) ? savedKey : defaultKey;
  const dir = savedDir === 'asc' || savedDir === 'desc' ? savedDir : defaultDir;

  selectedSort.value = validKey;
  sortDirections.value[validKey] = dir;

  const opt = essaySortOptions.find((o) => o.key === validKey);
  paramData.value.sort = `${opt.value}.${dir}`;
}

// ========================= 모바일 앱과 통신 =========================
/** 모바일 하단 여백 */
const insetBottom = ref(20);

onMounted(async () => {
  window.addEventListener('message', handleMessage);
  document.addEventListener('message', handleMessage);
});

onUnmounted(() => {
  window.removeEventListener('message', handleMessage);
  document.removeEventListener('message', handleMessage);
});

/** 모바일 앱과 통신 */
const handleMessage = async (event) => {
  try {
    let data;

    if (typeof event.data === 'string') {
      data = JSON.parse(event.data);
    } else {
      data = event.data;
    }

    if (data.source === 'react-devtools-content-script') return;
    if (data.key === '__VUE_DEVTOOLS_VUE_DETECTED_EVENT__') return;

    // 인증 상태는 글로벌 RN 메시지 핸들러에서만 관리한다.
    if (data.action === 'init') {
      if (data?.payload?.bottom) insetBottom.value = data?.payload?.bottom;
    }
  } catch (e) {
    console.error('Invalid message received:', `${e}`);
  }
};
</script>

<template>
  <div
    class="st-board-list st-essay-list"
    :style="isMobile ? { paddingBottom: `${insetBottom}px` } : {}"
  >
    <div
      :class="{
        'mobile-board__sticky-top': isMobile,
        'wrap-as-none': !isMobile,
      }"
    >
      <BoardTitleBox :board="board">
        <template v-slot:add-on>
          <!-- <SearchBox
            :placeholder="placeholder"
            :search-keywords="searchKeywords"
            @update:searchKeywords="updateSearchKeywords"
            @search="getList"
            @clear="onSearchClear"
          /> -->
          <SearchBoxWithList
            :key="recentKey"
            :placeholder="placeholder"
            :search-keywords="searchKeywords"
            :recent-key="recentKey"
            :recent="recent"
            @update:searchKeywords="updateSearchKeywords"
            @search="getList"
            @clear="onSearchClear"
          />
          <div v-if="isMobile" class="app-essay-header__bottom">
            <div class="essay-header__bottom-total">
              전체 <span class="essay__total-text">{{ totalElements }}</span>
            </div>
            <div class="essay-header__bottom-dropdown" ref="ddRef">
              <button
                class="dropdown__button"
                type="button"
                :aria-expanded="open ? 'true' : 'false'"
                aria-haspopup="listbox"
                aria-controls="essay-sort-menu"
                @click="toggle()"
              >
                <span class="dropdown__label">
                  {{ currentOption?.text ?? '정렬' }}
                </span>
                <span class="chev" :class="{ open }" aria-hidden="true"></span>
              </button>

              <ul
                v-show="open"
                id="essay-sort-menu"
                class="dropdown__menu"
                role="listbox"
                :aria-activedescendant="'opt-' + (currentOption?.key ?? '')"
              >
                <li v-for="opt in essaySortOptions" :key="opt.key">
                  <button
                    type="button"
                    class="dropdown__item"
                    :id="'opt-' + opt.key"
                    :class="{ active: selectedSort === opt.key }"
                    role="option"
                    @click="choose(opt)"
                  >
                    {{ opt.text }}
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </template>
      </BoardTitleBox>
    </div>
    <div v-if="!isMobile" class="board__search-results">
      <div class="search-result__total">
        결과 <span class="search-result__total">{{ totalElements }}</span
        >건
      </div>
      <div class="search-result__"></div>
      <div v-if="!isMobile" class="sort-seach-box">
        <button
          v-for="option in essaySortOptions"
          :key="option.key"
          :class="{ active: selectedSort === option.key }"
          @click="sort(option)"
        >
          {{ option.text }}
        </button>
      </div>
    </div>
    <div v-if="isPopSearchNoneBox" class="search-result__underline"></div>
    <div v-if="isPopSearchNoneBox" class="board-list-box board-list__none">
      검색 결과가 없습니다.
    </div>
    <div v-if="isPopSearchNoneBox == false" class="grid-container">
      <div
        v-if="!isMobile"
        v-for="(item, index) in listData"
        :key="index"
        class="item"
        @click="goView(item.essayId)"
      >
        <div class="img-box">
          <img
            v-if="item.attachFiles && item.attachFiles.length"
            :src="item.attachFiles[0].thumbnailUri || item.attachFiles[0].publishedUri"
            alt="attached file"
          />
          <div v-else :style="divStyle(item)"></div>
          <button class="txt-like" @click="(event) => handleBubbling(event, item.essayId)">
            <IconLike class="icon-like" :class="{ active: item.likesChecked }" />
          </button>
        </div>
        <div class="info-box">
          <div class="txt-tit">
            {{ item.firstTitle }}
          </div>
          <div class="txt-date">
            {{ formatCustomDate(item.displayDate) }}
          </div>
        </div>
      </div>
      <div
        v-if="isMobile"
        v-for="(item, index) in listData"
        :key="index"
        class="item app-essay__item"
        @click="goView(item.essayId)"
      >
        <div class="img-box">
          <img
            v-if="item.attachFiles && item.attachFiles.length"
            :src="item.attachFiles[0].thumbnailUri || item.attachFiles[0].publishedUri"
            alt="attached file"
          />
          <div v-else :style="divStyle(item)"></div>
          <button class="txt-like" @click="(event) => handleBubbling(event, item.essayId)">
            <IconLike class="icon-like" :class="{ active: item.likesChecked }" />
          </button>
        </div>
        <div class="info-box">
          <div class="app-essay__title">
            <div class="txt-tit">
              {{ item.firstTitle }}
            </div>
            <div class="txt-date">
              {{ formatCustomDate(item.displayDate) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <Pagination
      v-if="totalElements > 0 && listData.length > 0"
      :total-pages="totalPages"
      :current-page="paramData.page"
      :max-visible-pages="isMobile ? 5 : 10"
      @page-changed="changePage"
    />

    <div v-if="isLoading" class="loading-full-wrap">
      <div class="inner-box">
        <div ref="loadingContainer" class="loading-container">
          <v-progress-circular :size="50" :width="3" color="primary" indeterminate />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  opacity: 0.3;
  position: absolute;
  background: #fff;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
}
.grid-container .item .txt-date {
  color: #757575;
  font-weight: 500;
}
@media (max-width: 768px) {
  .st-essay-list .grid-container {
    gap: 8px;
    .item {
      position: relative;
      max-width: 100%;
      flex: 0 0 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0;
    }
  }
  .grid-container {
    grid-template-columns: repeat(1, 1fr);
    padding: 0 16px;
    gap: 8px;
  }
  .grid-container .app-essay__item {
    position: relative;
    border-radius: 8px;
    // background: linear-gradient(0deg, rgba(0, 0, 0, 0.40) 0%, rgba(0, 0, 0, 0.40) 100%), url(<path-to-image>) lightgray 50% / cover no-repeat;
    .img-box {
      max-height: 160px;
      img {
        width: 100%;
        height: 100%;
        filter: brightness(0.6);
      }
    }
    .app-essay__title {
      position: absolute;
      inset: 0;
      max-width: 100%;
      padding: 0 16px;
      display: inline-flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
      gap: 16px;
      .txt-tit {
        color: var(--gray-100-white, #fff);
        font-family: 'Nanum Myeongjo';
        font-size: 18px;
        font-style: normal;
        font-weight: 400;
        line-height: 22px;
        word-break: keep-all;
      }
      .txt-date {
        color: var(--Gray-300, #ebebeb);
        font-family: Pretendard;
        font-size: 13px;
        font-style: normal;
        font-weight: 400;
        line-height: 16px;
      }
    }
  }
}
</style>
